import { useSearchParams } from "react-router";

import { apiNavigateURL } from "../api/client";
import { useAuthProviders } from "../api/queries";
import { GitHubMark, GoogleMark, Icon } from "../components/Icons";
import type { Provider } from "../api/types.gen";

/**
 * Reasons the API redirects back here after a failed sign-in.
 *
 * The server sends coarse codes on purpose — anything more specific would tell
 * an attacker which half of the OAuth flow failed — so the copy is
 * correspondingly general, and always leaves the user something to do.
 */
const errorMessages: Record<string, string> = {
  expired: "That sign-in link expired. Please try again.",
  denied: "Sign-in was cancelled. Choose a provider to try again.",
  failed: "We could not complete sign-in. Please try again.",
  unknown_provider: "That sign-in method is not available.",
};

function providerMark(provider: Provider) {
  return provider === "github" ? <GitHubMark /> : <GoogleMark />;
}

/**
 * The sign-in screen, ported from design-prototype/app.js:loginView.
 *
 * The markup and class names are kept as the prototype produced them, because
 * src/styles/styles.css targets those classes directly and is the design
 * system. What changed: the two hardcoded buttons are now driven by
 * /api/v1/auth/providers, so a deployment with only GitHub configured shows
 * one button rather than a Google button that fails on click.
 */
export function Login() {
  const [params] = useSearchParams();
  const { data, isPending, isError } = useAuthProviders();

  const errorCode = params.get("error");
  const errorMessage = errorCode
    ? (errorMessages[errorCode] ?? errorMessages.failed)
    : null;

  // Preserved across the whole OAuth round trip inside the signed state, so a
  // user who was deep-linked lands where they were going.
  const returnTo = params.get("return_to") ?? "/";

  const providers = data?.providers ?? [];

  return (
    <div className="auth-page">
      <header className="auth-header">
        <a className="auth-brand" href="/">
          berbench<span>_</span>
        </a>
        <span className="auth-header-note">
          <i /> Repository coding agent benchmarks
        </span>
      </header>

      <main className="auth-layout">
        <section className="auth-story">
          <span className="auth-kicker"><i /> Benchmarks made from your code</span>
          <h1>Find the coding agent that works best for your team.</h1>
          <p>
            Turn completed pull requests into private, reproducible tests. Compare
            tools and models on the work that actually matters to you.
          </p>

          <div className="auth-proof">
            <div className="proof-window">
              <div className="proof-top">
                <i />
                <i />
                <i />
                <span>Sample benchmark report</span>
              </div>
              <div className="proof-score">
                <span>BER Index leader</span>
                <strong>Claude Code</strong>
                <small>opus-5 · medium effort</small>
                <em>92</em>
              </div>
              <div className="proof-row">
                <span>
                  <i className="pass" />
                  Hidden verifier
                </span>
                <strong>passed</strong>
              </div>
              <div className="proof-row">
                <span>Reported cost</span>
                <strong>$1.15</strong>
              </div>
            </div>

            <div className="auth-trust">
              <span>
                <Icon name="check" /> Your code stays in isolated containers
              </span>
              <span>
                <Icon name="check" /> Agents cannot fetch the original fix
              </span>
              <span>
                <Icon name="check" /> See cost before a run starts
              </span>
            </div>
          </div>
        </section>

        <section className="auth-card-wrap">
          <div className="auth-card">
            <div className="auth-card-head">
              <span className="auth-card-mark"><Icon name="lock" /></span>
              <span>
                <small>Secure workspace</small>
                <strong>Welcome to BERBench</strong>
              </span>
            </div>
            <h2>Start benchmarking your codebase</h2>
            <p>Sign in to create your workspace and set up your first benchmark.</p>

            {errorMessage && (
              <p className="auth-error" role="alert">
                {errorMessage}
              </p>
            )}

            {isPending && <p className="auth-loading">Loading sign-in options…</p>}

            {isError && (
              <p className="auth-error" role="alert">
                We could not reach the BERBench API. Please try again in a moment.
              </p>
            )}

            {!isPending && !isError && providers.length === 0 && (
              <p className="auth-error" role="alert">
                No sign-in provider is configured for this deployment.
              </p>
            )}

            {providers.map((p) => (
              <a
                key={p.provider}
                className={
                  p.provider === "github" ? "oauth-button github" : "oauth-button"
                }
                // A link, not a button with a fetch. The response is a 302 to
                // the provider and the state cookie must be set on a top-level
                // navigation; an XHR would follow the redirect invisibly and
                // land nowhere useful.
                href={apiNavigateURL(
                  `/api/v1/auth/${p.provider}/start?return_to=${encodeURIComponent(returnTo)}`,
                )}
              >
                {providerMark(p.provider)}
                <span>Continue with {p.label}</span>
              </a>
            ))}

            <div className="auth-divider">
              <span>secure sign in</span>
            </div>
            <small className="auth-terms">
              By continuing, you agree to the Terms of Service and Privacy Policy.
            </small>
          </div>
        </section>
      </main>
    </div>
  );
}
